import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/base/SEOHead';

export default function HomeAdaptationArticle() {
  const relatedArticles = [
    {
      title: 'Inkontinenz beim alten Hund: Was Sie wissen sollten',
      link: '/beduerfnisse/hygiene-inkontinenz',
      image: 'https://public.readdy.ai/ai/img_res/d46f45083e54e00c280b9a009e184ffc.jpg',
    },
    {
      title: 'Arthrose beim Hund erkennen und unterstützen',
      link: '/ratgeber',
      image: 'https://readdy.ai/api/search-image?query=senior%20dog%20being%20gently%20supported%20while%20walking%20showing%20mobility%20assistance%20and%20care%20warm%20home%20interior%20natural%20lighting%20realistic%20photography%20neutral%20tones&width=400&height=300&seq=related-arthro-002&orientation=landscape',
    },
    {
      title: 'Spaziergänge mit alten Hunden: Tempo und Dauer anpassen',
      link: '/ratgeber',
      image: 'https://readdy.ai/api/search-image?query=senior%20dog%20walking%20slowly%20on%20gentle%20path%20with%20owner%20showing%20adapted%20exercise%20routine%20peaceful%20outdoor%20setting%20soft%20natural%20lighting%20neutral%20tones&width=400&height=300&seq=related-walk-002&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Zuhause seniorengerecht gestalten für Hunde | Hund im Alter"
        description="So machen Sie Ihr Zuhause altersgerecht für Ihren Seniorhund: Rutschfeste Böden, Rampen, orthopädische Liegeplätze und mehr."
        keywords="Zuhause Seniorhund, Wohnung alter Hund, rutschfeste Matten Hund"
      />
      <Header />
      <main className="pt-32 pb-24">
        {/* Hero Section */}
        <div className="relative h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Senior%20dog%20with%20gray%20muzzle%20in%20specially%20adapted%20home%20environment%20showing%20non-slip%20rugs%20orthopedic%20bed%20easy%20access%20features%20warm%20interior%20natural%20lighting%20dignified%20elderly%20dog%20care%20soft%20earth%20tones%20beige%20cream%20colors%20safe%20comfortable%20atmosphere&width=1920&height=500&seq=home-adapt-hero-senior-001&orientation=landscape"
            alt="Das Zuhause seniorengerecht gestalten"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 max-w-5xl mx-auto px-6 lg:px-8 pb-12">
            <div className="inline-block px-4 py-2 bg-teal text-white text-sm font-semibold rounded-full mb-4">
              Alltag mit Seniorhunden
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              Das Zuhause seniorengerecht gestalten
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Kleine Anpassungen, große Wirkung: So machen Sie Ihr Zuhause altersgerecht für Ihren Seniorhund.
            </p>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Wenn Ihr Hund älter wird, verändern sich seine Bedürfnisse. Was früher kein Problem war – eine Treppe, glatte Böden oder ein niedriger Futternapf – kann plötzlich zur Herausforderung werden. Die gute Nachricht: Mit einigen durchdachten Anpassungen können Sie Ihrem Seniorhund das Leben deutlich erleichtern und ihm mehr Sicherheit und Komfort bieten.
            </p>
          </div>

          {/* Section 1 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              Warum ist ein seniorengerechtes Zuhause wichtig?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Mit zunehmendem Alter lassen Muskelkraft, Koordination und Gelenkbeweglichkeit nach. Viele Seniorhunde entwickeln Arthrose, haben Probleme mit dem Gleichgewicht oder leiden unter nachlassender Sehkraft. Diese Veränderungen machen alltägliche Situationen riskanter:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal mt-1"></i>
                <span className="text-lg text-gray-700"><strong>Rutschgefahr:</strong> Glatte Böden wie Fliesen oder Laminat werden zur Stolperfalle</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal mt-1"></i>
                <span className="text-lg text-gray-700"><strong>Treppen:</strong> Auf- und Absteigen belastet die Gelenke stark</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal mt-1"></i>
                <span className="text-lg text-gray-700"><strong>Unbequeme Liegeplätze:</strong> Harte Unterlagen führen zu Druckstellen und Schmerzen</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-teal mt-1"></i>
                <span className="text-lg text-gray-700"><strong>Schwer erreichbare Näpfe:</strong> Bücken beim Fressen und Trinken kann schmerzhaft sein</span>
              </li>
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed">
              Ein angepasstes Zuhause reduziert Sturzrisiken, entlastet die Gelenke und gibt Ihrem Hund mehr Selbstständigkeit und Lebensqualität.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              1. Rutschfeste Böden schaffen
            </h2>
            <div className="bg-sand rounded-2xl p-8 mb-6">
              <h3 className="text-xl font-bold text-navy mb-4">
                <i className="ri-alert-line text-teal mr-2"></i>
                Das Problem
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Ältere Hunde haben oft Schwierigkeiten, auf glatten Oberflächen Halt zu finden. Die Krallen nutzen sich weniger ab, die Muskulatur wird schwächer, und die Koordination lässt nach. Das Ergebnis: Ihr Hund rutscht, verliert das Gleichgewicht und vermeidet bestimmte Bereiche im Haus.
              </p>
            </div>
            <h3 className="text-2xl font-bold text-navy mb-4">Lösungen:</h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-checkbox-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Rutschfeste Matten und Läufer</h4>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Legen Sie rutschfeste Matten auf häufig genutzten Wegen aus – vom Schlafplatz zur Tür, vor dem Futternapf, im Flur. Achten Sie darauf, dass die Matten selbst nicht verrutschen (Anti-Rutsch-Unterlagen verwenden).
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-checkbox-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Anti-Rutsch-Socken oder Hundeschuhe</h4>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Spezielle Socken mit Gumminoppen geben Ihrem Hund mehr Grip. Besonders hilfreich bei Hunden, die sich an Matten nicht gewöhnen oder in mehreren Räumen unterwegs sind.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-checkbox-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Krallenpflege</h4>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Zu lange Krallen verschlechtern den Halt zusätzlich. Lassen Sie die Krallen regelmäßig kürzen – beim Tierarzt oder Hundefriseur.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              2. Treppen sichern oder vermeiden
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Treppen sind für Seniorhunde eine große Belastung. Jeder Schritt belastet die Gelenke, und das Sturzrisiko ist hoch. Wenn möglich, sollten Sie Treppen ganz vermeiden.
            </p>
            <h3 className="text-2xl font-bold text-navy mb-4">Praktische Maßnahmen:</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border-2 border-sand rounded-2xl p-6">
                <i className="ri-home-line text-4xl text-teal mb-4"></i>
                <h4 className="text-xl font-semibold text-navy mb-3">Schlafplatz verlegen</h4>
                <p className="text-base text-gray-700 leading-relaxed">
                  Richten Sie den Hauptschlafplatz im Erdgeschoss ein, damit Ihr Hund nicht mehr nach oben muss.
                </p>
              </div>
              <div className="bg-white border-2 border-sand rounded-2xl p-6">
                <i className="ri-door-line text-4xl text-teal mb-4"></i>
                <h4 className="text-xl font-semibold text-navy mb-3">Treppengitter installieren</h4>
                <p className="text-base text-gray-700 leading-relaxed">
                  Sperren Sie Treppen ab, um zu verhindern, dass Ihr Hund unbeaufsichtigt hinauf- oder hinuntergeht.
                </p>
              </div>
              <div className="bg-white border-2 border-sand rounded-2xl p-6">
                <i className="ri-hand-heart-line text-4xl text-teal mb-4"></i>
                <h4 className="text-xl font-semibold text-navy mb-3">Tragehilfe nutzen</h4>
                <p className="text-base text-gray-700 leading-relaxed">
                  Bei kleineren Hunden: Tragen Sie ihn die Treppe hinauf. Bei größeren: Verwenden Sie ein Tragegeschirr zur Unterstützung.
                </p>
              </div>
              <div className="bg-white border-2 border-sand rounded-2xl p-6">
                <i className="ri-footprint-line text-4xl text-teal mb-4"></i>
                <h4 className="text-xl font-semibold text-navy mb-3">Rutschfeste Stufenmatten</h4>
                <p className="text-base text-gray-700 leading-relaxed">
                  Falls Treppen unvermeidbar sind, bringen Sie rutschfeste Matten auf jeder Stufe an.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              3. Orthopädische Liegeplätze einrichten
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Seniorhunde verbringen mehr Zeit mit Ruhen und Schlafen. Ein guter Liegeplatz ist daher besonders wichtig, um Druckstellen, Gelenkschmerzen und Unbehagen zu vermeiden.
            </p>
            <div className="bg-teal/5 border-l-4 border-teal rounded-r-2xl p-6 mb-6">
              <h3 className="text-xl font-bold text-navy mb-3">
                <i className="ri-lightbulb-line text-teal mr-2"></i>
                Worauf Sie achten sollten:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal mt-1"></i>
                  <span className="text-lg text-gray-700"><strong>Memory-Schaum oder orthopädische Matratzen:</strong> Sie passen sich dem Körper an und entlasten die Gelenke</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal mt-1"></i>
                  <span className="text-lg text-gray-700"><strong>Ausreichende Polsterung:</strong> Mindestens 10 cm dick, damit der Hund nicht auf dem Boden aufliegt</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal mt-1"></i>
                  <span className="text-lg text-gray-700"><strong>Waschbare Bezüge:</strong> Wichtig bei Inkontinenz oder Sabbern</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal mt-1"></i>
                  <span className="text-lg text-gray-700"><strong>Niedriger Einstieg:</strong> Erhöhte Betten sollten eine Rampe oder niedrige Einstiegshöhe haben</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal mt-1"></i>
                  <span className="text-lg text-gray-700"><strong>Ruhige Lage:</strong> Platzieren Sie das Bett an einem zugfreien, ruhigen Ort mit Blick auf die Familie</span>
                </li>
              </ul>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              Tipp: Richten Sie mehrere Liegeplätze ein – einen im Wohnzimmer, einen im Schlafzimmer, eventuell einen im Garten. So kann Ihr Hund immer in Ihrer Nähe sein, ohne weite Wege zurücklegen zu müssen.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              4. Futter- und Wassernäpfe erhöhen
            </h2>
            <div className="flex flex-col md:flex-row gap-8 items-start mb-6">
              <div className="flex-1">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  Das Bücken zum Fressen und Trinken kann für Hunde mit Arthrose, Nackenproblemen oder Gelenkschmerzen sehr unangenehm sein. Erhöhte Näpfe bringen die Nahrung auf eine bequemere Höhe.
                </p>
                <h3 className="text-xl font-bold text-navy mb-3">Vorteile erhöhter Näpfe:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-teal mt-1"></i>
                    <span className="text-base text-gray-700">Entlastung von Nacken, Schultern und Vorderbeinen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-teal mt-1"></i>
                    <span className="text-base text-gray-700">Bessere Verdauung durch aufrechte Haltung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-teal mt-1"></i>
                    <span className="text-base text-gray-700">Weniger Verschlucken und Würgen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-teal mt-1"></i>
                    <span className="text-base text-gray-700">Mehr Komfort beim Fressen</span>
                  </li>
                </ul>
              </div>
              <div className="flex-1">
                <div className="bg-sand rounded-2xl p-6">
                  <h4 className="text-lg font-bold text-navy mb-3">
                    <i className="ri-ruler-line text-teal mr-2"></i>
                    Die richtige Höhe finden:
                  </h4>
                  <p className="text-base text-gray-700 leading-relaxed mb-4">
                    Die Napfoberkante sollte etwa auf Höhe der Schulter oder des unteren Brustbereichs Ihres Hundes sein. So kann er bequem fressen, ohne den Kopf stark zu senken oder zu heben.
                  </p>
                  <p className="text-sm text-gray-600 italic">
                    Tipp: Viele erhöhte Napfständer sind höhenverstellbar – ideal, wenn Sie die optimale Position erst austesten möchten.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              5. Beleuchtung verbessern
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Viele ältere Hunde sehen schlechter – besonders in der Dämmerung und nachts. Eine gute Beleuchtung hilft Ihrem Hund, sich sicherer zu bewegen und Hindernisse zu erkennen.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-full mx-auto mb-4">
                  <i className="ri-lightbulb-flash-line text-3xl text-teal"></i>
                </div>
                <h4 className="text-lg font-semibold text-navy mb-2">Nachtlichter</h4>
                <p className="text-base text-gray-700">
                  Platzieren Sie Nachtlichter in Fluren, vor Treppen und in der Nähe des Schlafplatzes
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-full mx-auto mb-4">
                  <i className="ri-sun-line text-3xl text-teal"></i>
                </div>
                <h4 className="text-lg font-semibold text-navy mb-2">Helle Räume</h4>
                <p className="text-base text-gray-700">
                  Sorgen Sie tagsüber für ausreichend Tageslicht oder helle Lampen
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-full mx-auto mb-4">
                  <i className="ri-flashlight-line text-3xl text-teal"></i>
                </div>
                <h4 className="text-lg font-semibold text-navy mb-2">Leuchthalsbänder</h4>
                <p className="text-base text-gray-700">
                  Im Garten oder bei Abendspaziergängen helfen leuchtende Halsbänder
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              6. Hindernisse entfernen
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Ältere Hunde sind weniger wendig und können Hindernissen schlechter ausweichen. Räumen Sie Stolperfallen aus dem Weg:
            </p>
            <div className="bg-white border-2 border-sand rounded-2xl p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                    <i className="ri-close-circle-line text-2xl text-red-500"></i>
                    Vermeiden Sie:
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span className="text-base text-gray-700">Herumliegende Kabel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span className="text-base text-gray-700">Niedrige Möbelstücke</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span className="text-base text-gray-700">Spielzeug auf dem Boden</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span className="text-base text-gray-700">Teppichkanten, die sich hochbiegen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      <span className="text-base text-gray-700">Enge Durchgänge</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-navy mb-3 flex items-center gap-2">
                    <i className="ri-check-circle-line text-2xl text-teal"></i>
                    Besser:
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">✓</span>
                      <span className="text-base text-gray-700">Kabel mit Kabelkanälen sichern</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">✓</span>
                      <span className="text-base text-gray-700">Freie Laufwege schaffen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">✓</span>
                      <span className="text-base text-gray-700">Spielzeug in Kisten aufbewahren</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">✓</span>
                      <span className="text-base text-gray-700">Teppiche mit Anti-Rutsch-Unterlagen fixieren</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-teal mt-1">✓</span>
                      <span className="text-base text-gray-700">Möbel so stellen, dass breite Wege bleiben</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              7. Zugang zum Garten erleichtern
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Wenn Ihr Hund Schwierigkeiten hat, nach draußen zu gelangen, wird er seltener rausgehen – was zu Inkontinenzproblemen oder Bewegungsmangel führen kann.
            </p>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-arrow-up-down-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Rampen statt Stufen</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Bauen Sie eine Rampe über Treppenstufen zur Terrasse oder zum Garten. Achten Sie auf eine sanfte Steigung (maximal 20 Grad) und rutschfeste Oberfläche.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-door-open-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Hundeklappe</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Eine Hundeklappe ermöglicht Ihrem Hund, selbstständig nach draußen zu gehen – besonders wichtig bei Inkontinenz oder häufigem Harndrang.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-teal/10 rounded-full">
                  <i className="ri-plant-line text-2xl text-teal"></i>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-navy mb-2">Sichere Gartenwege</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Legen Sie rutschfeste Wege im Garten an (z. B. mit Rindenmulch oder Gummimatten). Entfernen Sie spitze Steine oder Wurzeln, über die Ihr Hund stolpern könnte.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Checklist Section */}
          <section className="mb-16">
            <div className="bg-gradient-to-br from-teal/10 to-sand rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-navy mb-6 text-center">
                <i className="ri-checkbox-multiple-line text-teal mr-3"></i>
                Checkliste: Ist Ihr Zuhause seniorengerecht?
              </h2>
              <p className="text-lg text-gray-700 text-center mb-8 max-w-2xl mx-auto">
                Gehen Sie diese Punkte durch, um zu prüfen, wo noch Anpassungsbedarf besteht:
              </p>
              <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Rutschfeste Matten auf glatten Böden</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Treppen gesichert oder vermieden</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Orthopädischer Liegeplatz vorhanden</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Erhöhte Futter- und Wassernäpfe</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Ausreichende Beleuchtung (auch nachts)</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Hindernisse und Stolperfallen entfernt</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Einfacher Zugang zum Garten</span>
                </div>
                <div className="bg-white rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-blank-circle-line text-xl text-teal mt-1"></i>
                  <span className="text-base text-gray-700">Ruhige, zugfreie Liegeplätze</span>
                </div>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-navy mb-6">
              Fazit: Kleine Änderungen, große Wirkung
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Ein seniorengerechtes Zuhause muss nicht teuer oder aufwendig sein. Oft reichen schon ein paar rutschfeste Matten, ein orthopädisches Hundebett und erhöhte Näpfe, um Ihrem Hund den Alltag deutlich zu erleichtern. Beobachten Sie Ihren Hund genau: Wo hat er Schwierigkeiten? Welche Bereiche meidet er? Diese Hinweise zeigen Ihnen, wo Anpassungen am sinnvollsten sind.
            </p>
            <div className="bg-teal/5 border-l-4 border-teal rounded-r-2xl p-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong className="text-navy">Wichtig:</strong> Jeder Hund altert anders. Was für den einen hilfreich ist, braucht der andere vielleicht nicht. Passen Sie Ihr Zuhause individuell an die Bedürfnisse Ihres Hundes an – und scheuen Sie sich nicht, Dinge auszuprobieren und anzupassen. Ihr Hund wird es Ihnen mit mehr Lebensqualität und Wohlbefinden danken.
              </p>
            </div>
          </section>

          {/* Author & Date */}
          <div className="border-t border-gray-200 pt-8 mb-12">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center bg-teal/10 rounded-full">
                <i className="ri-user-heart-line text-3xl text-teal"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy">Hund im Alter Redaktion</p>
                <p className="text-sm text-gray-600">Aktualisiert: Januar 2025</p>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          <div className="border-t border-gray-200 pt-12">
            <h3 className="text-2xl font-bold text-navy mb-8">
              Das könnte Sie auch interessieren
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.link}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="relative w-full h-40">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="text-lg font-bold text-navy mb-2 leading-snug line-clamp-2 group-hover:text-teal transition-colors">
                      {article.title}
                    </h4>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-teal">
                      Weiterlesen
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Back to Guide */}
          <div className="mt-12 text-center">
            <Link
              to="/ratgeber"
              className="inline-flex items-center gap-2 text-base font-medium text-teal hover:gap-3 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Zurück zur Ratgeber-Übersicht
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
